'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImperativeEditorRef } from '../editor/lexical';
import type { MessageHistoryEntry } from '../editor/lexical/hooks/createMessageHistoryController';
import type { MentionEntityWithOffset } from '../editor/lexical/plugins/buildMessageSegments';
import { useMentions } from '../mentions/ChatMentionsProvider';

const editorStateCache = new Map<string, string>();

export function useChatComposerEditor({
  instanceId,
  isActive,
  onInputChange,
}: {
  instanceId?: string;
  isActive: boolean;
  onInputChange?: (value: string) => void;
}) {
  const [input, setInput] = useState('');
  const onInputChangeRef = useRef(onInputChange);

  onInputChangeRef.current = onInputChange;

  const editorRef = useRef<ImperativeEditorRef | null>(null);
  const inlineEditorRef = useRef<ImperativeEditorRef | null>(null);
  const {
    setInsertMentionInEditor,
    setGetMentionsFromEditor,
    syncMentionsWithEditor,
  } = useMentions();
  const latestEditorStateRef = useRef<string | null>(null);
  const pendingEditorStateRef = useRef<string | null>(null);
  const restoredEditorMentionsRef = useRef<MentionEntityWithOffset[]>([]);
  const stateSubscriptionCleanupRef = useRef<(() => void) | null>(null);
  const mentionSyncCleanupRef = useRef<(() => void) | null>(null);
  const syncMentionsWithEditorRef = useRef(syncMentionsWithEditor);

  syncMentionsWithEditorRef.current = syncMentionsWithEditor;

  const insertMentionInActiveEditor = useCallback(
    (entity: Parameters<ImperativeEditorRef['insertMention']>[0]) => {
      const target = inlineEditorRef.current ?? editorRef.current;

      if (!target) {
        return false;
      }

      target.insertMention(entity);

      return true;
    },
    [],
  );

  const editorRefCallback = useCallback(
    (instance: ImperativeEditorRef | null) => {
      stateSubscriptionCleanupRef.current?.();
      stateSubscriptionCleanupRef.current = null;
      mentionSyncCleanupRef.current?.();
      mentionSyncCleanupRef.current = null;
      editorRef.current = instance;
      if (instance) {
        stateSubscriptionCleanupRef.current = instance.subscribeToChanges(
          (state) => {
            latestEditorStateRef.current = state;
            const content = instance.getContent();

            setInput(content);
            onInputChangeRef.current?.(content);
          },
        );
        mentionSyncCleanupRef.current = instance.subscribeToChanges(() => {
          const editorEntityIds = new Set(
            (editorRef.current?.getMentions() ?? []).map(
              (mention) => mention.entityId,
            ),
          );

          syncMentionsWithEditorRef.current(editorEntityIds);
        });
        const pending = pendingEditorStateRef.current;

        if (pending) {
          pendingEditorStateRef.current = null;
          setTimeout(() => {
            instance.setEditorState(pending);
          }, 0);
        }

        if (isActive) {
          setGetMentionsFromEditor(() => instance.getMentions());
          setInsertMentionInEditor(insertMentionInActiveEditor);
        }
      }
    },
    [
      insertMentionInActiveEditor,
      isActive,
      setGetMentionsFromEditor,
      setInsertMentionInEditor,
    ],
  );

  const inlineEditorRefCallback = useCallback(
    (instance: ImperativeEditorRef | null) => {
      inlineEditorRef.current = instance;
    },
    [],
  );

  useEffect(() => {
    if (!isActive) return;
    setInsertMentionInEditor(insertMentionInActiveEditor);

    return () => setInsertMentionInEditor(undefined);
  }, [insertMentionInActiveEditor, isActive, setInsertMentionInEditor]);

  useEffect(() => {
    if (!isActive) return;
    setGetMentionsFromEditor(() => {
      const editorMentions = editorRef.current?.getMentions() ?? [];

      if (editorMentions.length > 0) {
        restoredEditorMentionsRef.current = [];

        return editorMentions;
      }

      return restoredEditorMentionsRef.current;
    });

    return () => setGetMentionsFromEditor(undefined);
  }, [isActive, setGetMentionsFromEditor]);

  const handleMessageHistoryEntryRestored = useCallback(
    (entry: MessageHistoryEntry) => {
      restoredEditorMentionsRef.current = entry.editorMentions;
    },
    [],
  );

  useEffect(() => {
    if (!instanceId) return;
    const cached = editorStateCache.get(instanceId);

    if (cached) {
      latestEditorStateRef.current = cached;
      pendingEditorStateRef.current = cached;
      const id = setTimeout(() => {
        const pending = pendingEditorStateRef.current;

        if (pending && editorRef.current) {
          pendingEditorStateRef.current = null;
          editorRef.current.setEditorState(pending);
        }
      }, 0);

      return () => {
        clearTimeout(id);
        pendingEditorStateRef.current = null;
      };
    }
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return;

    return () => {
      const state = latestEditorStateRef.current;

      if (state) {
        editorStateCache.set(instanceId, state);
      } else {
        editorStateCache.delete(instanceId);
      }
    };
  }, [instanceId]);

  return {
    input,
    setInput,
    editorRef,
    editorRefCallback,
    inlineEditorRefCallback,
    handleMessageHistoryEntryRestored,
  };
}
